import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Center,
  Field,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { InputField, SelectInput, TextareaField } from '@themes/aegix';
import { Switch } from '@chakra-ui/react';
import {
  DataTable,
  TabMenu,
  Button as ButtonComponent,
  type TabItem,
} from '@themes/aegix';
import { DrawerComponent } from '../ui/drawerComponent';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  carRiderFormSchema,
  pickupDelegationFormSchema,
} from '../../validations/visitorManagement.validation';
import type {
  VisitReasonTypeWrite,
  VisitorCategoryTypeWrite,
  BadgeTemplateWrite,
  ScreeningConfigurationWrite,
  EmployeeReasonTypeWrite,
  CarRiderQueueEntryWrite,
  PickupDelegationWrite,
} from '../../types/VisitorManagementTypes';
import { RosterContent } from '../visitors/RosterContent';
import { useReasonTypeCrud } from './hooks/useReasonTypeCrud';
import { useCategoryTypeCrud } from './hooks/useCategoryTypeCrud';
import { useBadgeTemplateCrud } from './hooks/useBadgeTemplateCrud';
import { useScreeningConfigCrud } from './hooks/useScreeningConfigCrud';
import { useEmployeeReasonTypeCrud } from './hooks/useEmployeeReasonTypeCrud';
import { useCarRiderQueueCrud } from './hooks/useCarRiderQueueCrud';
import { usePickupDelegationCrud } from './hooks/usePickupDelegationCrud';
import { usePlanningStudents } from '../../hooks/queries/useReunification';
import { useSearchParams } from 'react-router-dom';
import type { PlanningStudent } from '../../types/ReunificationTypes';
import { useVisitorPermissions } from '../../hooks/useVisitorPermissions';

// =============================================================================
// Sub-tab definitions
// =============================================================================

const vmTabs: TabItem[] = [
  {
    value: 'visit-reasons',
    label: 'Visit Reasons',
    'data-e2e': 'tab-visit-reasons',
  },
  {
    value: 'employee-reasons',
    label: 'Employee Reasons',
    'data-e2e': 'tab-employee-reasons',
  },
  { value: 'categories', label: 'Categories', 'data-e2e': 'tab-categories' },
  {
    value: 'badge-templates',
    label: 'Badge Templates',
    'data-e2e': 'tab-badge-templates',
  },
  { value: 'screening', label: 'Screening', 'data-e2e': 'tab-screening' },
  { value: 'roster', label: 'Roster', 'data-e2e': 'tab-roster' },
  { value: 'car-rider', label: 'Car Rider', 'data-e2e': 'tab-car-rider' },
  {
    value: 'pickup-delegation',
    label: 'Pickup Delegation',
    'data-e2e': 'tab-pickup-delegation',
  },
];
// =============================================================================
// Shared CRUD table helpers
// =============================================================================

function ErrorPlaceholder({ error }: { error: unknown }) {
  return (
    <Center py={12}>
      <Text color="text.danger">
        {error instanceof Error ? error.message : 'Failed to load data'}
      </Text>
    </Center>
  );
}

function EmptyPlaceholder({ message }: { message: string }) {
  return (
    <Center py={12}>
      <Text color="text.secondary">{message}</Text>
    </Center>
  );
}

function LoadingPlaceholder() {
  return (
    <Center py={12}>
      <Spinner size="lg" color="button.primary" />
    </Center>
  );
}

// =============================================================================
// Visit Reasons sub-tab
// =============================================================================

function ReasonTypesSection() {
  const { t } = useTranslation();
  const {
    isLoading,
    error,
    drawerOpen,
    setDrawerOpen,
    editItem,
    handleClose,
    handleSubmit,
    handleDelete,
    handleRowClick,
    initialValues,
    rows,
    columns,
  } = useReasonTypeCrud();

  const methods = useForm<VisitReasonTypeWrite>({
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  const {
    register,
    control,
    formState: { errors },
  } = methods;

  useEffect(() => {
    methods.reset(initialValues);
  }, [initialValues]);

  const onSubmit = (values: VisitReasonTypeWrite) => {
    handleSubmit(values, methods.setError);
  };

  return (
    <Box>
      <Box px={5} pb={2} pt={4} display="flex" justifyContent="flex-end">
        <ButtonComponent
          visual="primary"
          onClick={() => setDrawerOpen(true)}
          data-e2e="add-reason-button"
        >
          Add Reason
        </ButtonComponent>
      </Box>
      <Box px={5} pb={5}>
        {isLoading ? (
          <LoadingPlaceholder />
        ) : error ? (
          <ErrorPlaceholder error={error} />
        ) : rows.length === 0 ? (
          <EmptyPlaceholder message="No visit reason types configured" />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            keyExtractor={(row) => row.id}
            onRowClick={handleRowClick}
          />
        )}
      </Box>

      <DrawerComponent
        isOpen={drawerOpen}
        onClose={handleClose}
        title={editItem ? 'Edit Visit Reason' : 'Add Visit Reason'}
        size="md"
        footer={
          <SimpleGrid columns={editItem ? 3 : 2} gap={4} width="100%">
            {editItem && (
              <ButtonComponent
                visual="alert"
                type="button"
                onClick={handleDelete}
                data-e2e="delete-button"
              >
                Delete
              </ButtonComponent>
            )}
            <ButtonComponent
              visual="secondary"
              type="button"
              onClick={handleClose}
              data-e2e="cancel-button"
            >
              Cancel
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              type="submit"
              form="reason-form"
              data-e2e="save-button"
            >
              Save
            </ButtonComponent>
          </SimpleGrid>
        }
      >
        <FormProvider {...methods}>
          <form id="reason-form" onSubmit={methods.handleSubmit(onSubmit)}>
            <VStack gap={4} align="stretch">
              <Field.Root invalid={!!errors.name}>
                <Field.Label htmlFor="reason-name">Name</Field.Label>
                <InputField
                  id="reason-name"
                  {...register('name')}
                  placeholder="e.g. Meeting"
                  data-e2e="eg-meeting-input"
                  aria-invalid={!!errors.name}
                  aria-describedby={
                    errors.name ? 'reason-name-error' : undefined
                  }
                />
                {errors.name && (
                  <Field.ErrorText
                    id="reason-name-error"
                    data-e2e="visitor-reason-name-error"
                  >
                    {errors.name.message ? t(errors.name.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>
              <Field.Root invalid={!!errors.code}>
                <Field.Label htmlFor="reason-code">Code</Field.Label>
                <InputField
                  id="reason-code"
                  {...register('code')}
                  placeholder="e.g. meeting"
                  data-e2e="eg-meeting-input"
                  aria-invalid={!!errors.code}
                  aria-describedby={
                    errors.code ? 'reason-code-error' : undefined
                  }
                />
                {errors.code && (
                  <Field.ErrorText
                    id="reason-code-error"
                    data-e2e="visitor-reason-code-error"
                  >
                    {errors.code.message ? t(errors.code.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>
              <Field.Root invalid={!!errors.description}>
                <Field.Label htmlFor="reason-description">
                  Description
                </Field.Label>
                <TextareaField
                  id="reason-description"
                  {...register('description')}
                  rows={2}
                  data-e2e="reason-description-textarea"
                  aria-invalid={!!errors.description}
                  aria-describedby={
                    errors.description ? 'reason-description-error' : undefined
                  }
                />
                {errors.description && (
                  <Field.ErrorText
                    id="reason-description-error"
                    data-e2e="visitor-reason-description-error"
                  >
                    {errors.description.message
                      ? t(errors.description.message)
                      : null}
                  </Field.ErrorText>
                )}
              </Field.Root>
              <Field.Root>
                <Controller
                  name="requires_host"
                  control={control}
                  render={({ field }) => (
                    <Switch.Root
                      checked={field.value}
                      onCheckedChange={(d) => field.onChange(d.checked)}
                    >
                      <Switch.HiddenInput data-e2e="reason-requires-host-switch" />
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                      <Switch.Label>Requires Host</Switch.Label>
                    </Switch.Root>
                  )}
                />
              </Field.Root>
              <Field.Root>
                <Controller
                  name="requires_escort"
                  control={control}
                  render={({ field }) => (
                    <Switch.Root
                      checked={field.value}
                      onCheckedChange={(d) => field.onChange(d.checked)}
                    >
                      <Switch.HiddenInput data-e2e="reason-requires-escort-switch" />
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                      <Switch.Label>Requires Escort</Switch.Label>
                    </Switch.Root>
                  )}
                />
              </Field.Root>
              <Field.Root>
                <Controller
                  name="is_active"
                  control={control}
                  render={({ field }) => (
                    <Switch.Root
                      checked={field.value}
                      onCheckedChange={(d) => field.onChange(d.checked)}
                    >
                      <Switch.HiddenInput data-e2e="reason-active-switch" />
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                      <Switch.Label>Active</Switch.Label>
                    </Switch.Root>
                  )}
                />
              </Field.Root>
            </VStack>
          </form>
        </FormProvider>
      </DrawerComponent>
    </Box>
  );
}

// =============================================================================
// Visitor Categories sub-tab
// =============================================================================

function CategoryTypesSection() {
  const { t } = useTranslation();
  const {
    isLoading,
    error,
    drawerOpen,
    setDrawerOpen,
    editItem,
    handleClose,
    handleSubmit,
    handleDelete,
    handleRowClick,
    initialValues,
    rows,
    columns,
  } = useCategoryTypeCrud();

  const methods = useForm<VisitorCategoryTypeWrite>({
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  const {
    register,
    control,
    formState: { errors },
  } = methods;

  useEffect(() => {
    methods.reset(initialValues);
  }, [initialValues]);

  const onSubmit = (values: VisitorCategoryTypeWrite) => {
    handleSubmit(values, methods.setError);
  };

  return (
    <Box>
      <Box px={5} pb={2} pt={4} display="flex" justifyContent="flex-end">
        <ButtonComponent
          visual="primary"
          onClick={() => setDrawerOpen(true)}
          data-e2e="add-category-button"
        >
          Add Category
        </ButtonComponent>
      </Box>
      <Box px={5} pb={5}>
        {isLoading ? (
          <LoadingPlaceholder />
        ) : error ? (
          <ErrorPlaceholder error={error} />
        ) : rows.length === 0 ? (
          <EmptyPlaceholder message="No visitor category types configured" />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            keyExtractor={(row) => row.id}
            onRowClick={handleRowClick}
          />
        )}
      </Box>

      <DrawerComponent
        isOpen={drawerOpen}
        onClose={handleClose}
        title={editItem ? 'Edit Category' : 'Add Category'}
        size="md"
        footer={
          <SimpleGrid columns={editItem ? 3 : 2} gap={4} width="100%">
            {editItem && (
              <ButtonComponent
                visual="alert"
                type="button"
                onClick={handleDelete}
                data-e2e="delete-button"
              >
                Delete
              </ButtonComponent>
            )}
            <ButtonComponent
              visual="secondary"
              type="button"
              onClick={handleClose}
              data-e2e="cancel-button"
            >
              Cancel
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              type="submit"
              form="category-form"
              data-e2e="save-button"
            >
              Save
            </ButtonComponent>
          </SimpleGrid>
        }
      >
        <FormProvider {...methods}>
          <form id="category-form" onSubmit={methods.handleSubmit(onSubmit)}>
            <VStack gap={4} align="stretch">
              <Field.Root invalid={!!errors.name}>
                <Field.Label htmlFor="category-name">Name</Field.Label>
                <InputField
                  id="category-name"
                  {...register('name')}
                  placeholder="e.g. Parent"
                  data-e2e="eg-parent-input"
                  aria-invalid={!!errors.name}
                  aria-describedby={
                    errors.name ? 'category-name-error' : undefined
                  }
                />
                {errors.name && (
                  <Field.ErrorText
                    id="category-name-error"
                    data-e2e="visitor-category-name-error"
                  >
                    {errors.name.message ? t(errors.name.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>
              <Field.Root invalid={!!errors.code}>
                <Field.Label htmlFor="category-code">Code</Field.Label>
                <InputField
                  id="category-code"
                  {...register('code')}
                  placeholder="e.g. parent"
                  data-e2e="eg-parent-input"
                  aria-invalid={!!errors.code}
                  aria-describedby={
                    errors.code ? 'category-code-error' : undefined
                  }
                />
                {errors.code && (
                  <Field.ErrorText
                    id="category-code-error"
                    data-e2e="visitor-category-code-error"
                  >
                    {errors.code.message ? t(errors.code.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>
              <Field.Root invalid={!!errors.description}>
                <Field.Label htmlFor="category-description">
                  Description
                </Field.Label>
                <TextareaField
                  id="category-description"
                  {...register('description')}
                  rows={2}
                  data-e2e="category-description-textarea"
                  aria-invalid={!!errors.description}
                  aria-describedby={
                    errors.description
                      ? 'category-description-error'
                      : undefined
                  }
                />
                {errors.description && (
                  <Field.ErrorText
                    id="category-description-error"
                    data-e2e="visitor-category-description-error"
                  >
                    {errors.description.message
                      ? t(errors.description.message)
                      : null}
                  </Field.ErrorText>
                )}
              </Field.Root>
              <Field.Root>
                <Controller
                  name="requires_background_check"
                  control={control}
                  render={({ field }) => (
                    <Switch.Root
                      checked={field.value}
                      onCheckedChange={(d) => field.onChange(d.checked)}
                    >
                      <Switch.HiddenInput data-e2e="category-background-check-switch" />
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                      <Switch.Label>Requires Background Check</Switch.Label>
                    </Switch.Root>
                  )}
                />
              </Field.Root>
              <Field.Root>
                <Controller
                  name="auto_detect_from_sis"
                  control={control}
                  render={({ field }) => (
                    <Switch.Root
                      checked={field.value}
                      onCheckedChange={(d) => field.onChange(d.checked)}
                    >
                      <Switch.HiddenInput data-e2e="category-sis-detect-switch" />
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                      <Switch.Label>Auto-Detect from SIS</Switch.Label>
                    </Switch.Root>
                  )}
                />
              </Field.Root>
              <Field.Root>
                <Controller
                  name="is_active"
                  control={control}
                  render={({ field }) => (
                    <Switch.Root
                      checked={field.value}
                      onCheckedChange={(d) => field.onChange(d.checked)}
                    >
                      <Switch.HiddenInput data-e2e="category-active-switch" />
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                      <Switch.Label>Active</Switch.Label>
                    </Switch.Root>
                  )}
                />
              </Field.Root>
            </VStack>
          </form>
        </FormProvider>
      </DrawerComponent>
    </Box>
  );
}

// =============================================================================
// Badge Templates sub-tab
// =============================================================================

function BadgeTemplatesSection() {
  const { t } = useTranslation();
  const {
    isLoading,
    error,
    drawerOpen,
    setDrawerOpen,
    editItem,
    handleClose,
    handleSubmit,
    handleDelete,
    handleRowClick,
    initialValues,
    rows,
    columns,
  } = useBadgeTemplateCrud();

  const methods = useForm<BadgeTemplateWrite>({
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  const {
    register,
    control,
    formState: { errors },
  } = methods;

  useEffect(() => {
    methods.reset(initialValues);
  }, [initialValues]);

  const onSubmit = (values: BadgeTemplateWrite) => {
    handleSubmit(values, methods.setError);
  };

  return (
    <Box>
      <Box px={5} pb={2} pt={4} display="flex" justifyContent="flex-end">
        <ButtonComponent
          visual="primary"
          onClick={() => setDrawerOpen(true)}
          data-e2e="add-template-button"
        >
          Add Template
        </ButtonComponent>
      </Box>
      <Box px={5} pb={5}>
        {isLoading ? (
          <LoadingPlaceholder />
        ) : error ? (
          <ErrorPlaceholder error={error} />
        ) : rows.length === 0 ? (
          <EmptyPlaceholder message="No badge templates configured" />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            keyExtractor={(row) => row.id}
            onRowClick={handleRowClick}
          />
        )}
      </Box>

      <DrawerComponent
        isOpen={drawerOpen}
        onClose={handleClose}
        title={editItem ? 'Edit Badge Template' : 'Add Badge Template'}
        size="md"
        footer={
          <SimpleGrid columns={editItem ? 3 : 2} gap={4} width="100%">
            {editItem && (
              <ButtonComponent
                visual="alert"
                type="button"
                onClick={handleDelete}
                data-e2e="delete-button"
              >
                Delete
              </ButtonComponent>
            )}
            <ButtonComponent
              visual="secondary"
              type="button"
              onClick={handleClose}
              data-e2e="cancel-button"
            >
              Cancel
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              type="submit"
              form="badge-form"
              data-e2e="save-button"
            >
              Save
            </ButtonComponent>
          </SimpleGrid>
        }
      >
        <FormProvider {...methods}>
          <form id="badge-form" onSubmit={methods.handleSubmit(onSubmit)}>
            <VStack gap={4} align="stretch">
              <Field.Root invalid={!!errors.name}>
                <Field.Label htmlFor="template-name">Template Name</Field.Label>
                <InputField
                  id="template-name"
                  {...register('name')}
                  placeholder="e.g. Standard Visitor Badge"
                  data-e2e="eg-standard-visitor-badge-input"
                  aria-invalid={!!errors.name}
                  aria-describedby={
                    errors.name ? 'template-name-error' : undefined
                  }
                />
                {errors.name && (
                  <Field.ErrorText
                    id="template-name-error"
                    data-e2e="visitor-badge-name-error"
                  >
                    {errors.name.message ? t(errors.name.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>
              <SimpleGrid columns={2} gap={4}>
                <Field.Root invalid={!!errors.width_inches}>
                  <Field.Label htmlFor="template-width">
                    Width (inches)
                  </Field.Label>
                  <InputField
                    id="template-width"
                    {...register('width_inches', { valueAsNumber: true })}
                    type="number"
                    step={0.25}
                    data-e2e="badge-width-input"
                    aria-invalid={!!errors.width_inches}
                    aria-describedby={
                      errors.width_inches ? 'template-width-error' : undefined
                    }
                  />
                  {errors.width_inches && (
                    <Field.ErrorText
                      id="template-width-error"
                      data-e2e="visitor-badge-width-error"
                    >
                      {errors.width_inches.message
                        ? t(errors.width_inches.message)
                        : null}
                    </Field.ErrorText>
                  )}
                </Field.Root>
                <Field.Root invalid={!!errors.height_inches}>
                  <Field.Label htmlFor="template-height">
                    Height (inches)
                  </Field.Label>
                  <InputField
                    id="template-height"
                    {...register('height_inches', { valueAsNumber: true })}
                    type="number"
                    step={0.25}
                    data-e2e="badge-height-input"
                    aria-invalid={!!errors.height_inches}
                    aria-describedby={
                      errors.height_inches ? 'template-height-error' : undefined
                    }
                  />
                  {errors.height_inches && (
                    <Field.ErrorText
                      id="template-height-error"
                      data-e2e="visitor-badge-height-error"
                    >
                      {errors.height_inches.message
                        ? t(errors.height_inches.message)
                        : null}
                    </Field.ErrorText>
                  )}
                </Field.Root>
              </SimpleGrid>
              <Field.Root>
                <Controller
                  name="is_default"
                  control={control}
                  render={({ field }) => (
                    <Switch.Root
                      checked={field.value}
                      onCheckedChange={(d) => field.onChange(d.checked)}
                    >
                      <Switch.HiddenInput data-e2e="badge-default-switch" />
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                      <Switch.Label>Default Template</Switch.Label>
                    </Switch.Root>
                  )}
                />
              </Field.Root>
              <Field.Root>
                <Controller
                  name="is_active"
                  control={control}
                  render={({ field }) => (
                    <Switch.Root
                      checked={field.value}
                      onCheckedChange={(d) => field.onChange(d.checked)}
                    >
                      <Switch.HiddenInput data-e2e="badge-active-switch" />
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                      <Switch.Label>Active</Switch.Label>
                    </Switch.Root>
                  )}
                />
              </Field.Root>
            </VStack>
          </form>
        </FormProvider>
      </DrawerComponent>
    </Box>
  );
}

// =============================================================================
// Screening Configuration sub-tab
// =============================================================================

function ScreeningConfigSection() {
  const { t } = useTranslation();
  const { isLoading, error, handleSubmit, initialValues } =
    useScreeningConfigCrud();

  const methods = useForm<ScreeningConfigurationWrite>({
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  const {
    register,
    control,
    watch,
    formState: { errors },
  } = methods;

  useEffect(() => {
    methods.reset(initialValues);
  }, [initialValues]);

  const isEnabled = watch('is_enabled');
  const stateRegistryEnabled = watch('state_registry_enabled');

  const onSubmit = (values: ScreeningConfigurationWrite) => {
    handleSubmit(values, methods.setError);
  };

  if (isLoading) return <LoadingPlaceholder />;
  if (error) return <ErrorPlaceholder error={error} />;

  return (
    <Box px={5} py={4} maxW="600px">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <VStack gap={5} align="stretch">
            <Field.Root>
              <Controller
                name="is_enabled"
                control={control}
                render={({ field }) => (
                  <Switch.Root
                    checked={field.value}
                    onCheckedChange={(d) => field.onChange(d.checked)}
                  >
                    <Switch.HiddenInput data-e2e="screening-enabled-switch" />
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                    <Switch.Label>Enable Background Screening</Switch.Label>
                  </Switch.Root>
                )}
              />
            </Field.Root>

            <Field.Root>
              <Controller
                name="auto_screen_on_checkin"
                control={control}
                render={({ field }) => (
                  <Switch.Root
                    checked={field.value}
                    onCheckedChange={(d) => field.onChange(d.checked)}
                    disabled={!isEnabled}
                  >
                    <Switch.HiddenInput data-e2e="screening-auto-screen-switch" />
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                    <Switch.Label>Auto-Screen on Check-In</Switch.Label>
                  </Switch.Root>
                )}
              />
            </Field.Root>

            <Field.Root invalid={!!errors.cache_ttl_hours}>
              <Field.Label htmlFor="screening-cache-ttl">
                Cache TTL (hours)
              </Field.Label>
              <InputField
                id="screening-cache-ttl"
                {...register('cache_ttl_hours', { valueAsNumber: true })}
                type="number"
                min={1}
                max={168}
                disabled={!isEnabled}
                data-e2e="screening-cache-ttl-input"
                aria-invalid={!!errors.cache_ttl_hours}
                aria-describedby={
                  errors.cache_ttl_hours
                    ? 'screening-cache-ttl-helper screening-cache-ttl-error'
                    : 'screening-cache-ttl-helper'
                }
              />
              <Field.HelperText id="screening-cache-ttl-helper">
                How long screening results are cached before re-screening
              </Field.HelperText>
              {errors.cache_ttl_hours && (
                <Field.ErrorText
                  id="screening-cache-ttl-error"
                  data-e2e="screening-cache-ttl-error"
                >
                  {errors.cache_ttl_hours.message
                    ? t(errors.cache_ttl_hours.message)
                    : null}
                </Field.ErrorText>
              )}
            </Field.Root>

            <Field.Root>
              <Controller
                name="nsoprc_enabled"
                control={control}
                render={({ field }) => (
                  <Switch.Root
                    checked={field.value}
                    onCheckedChange={(d) => field.onChange(d.checked)}
                    disabled={!isEnabled}
                  >
                    <Switch.HiddenInput data-e2e="screening-nsoprc-switch" />
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                    <Switch.Label>
                      NSOPRC (National Sex Offender Registry)
                    </Switch.Label>
                  </Switch.Root>
                )}
              />
            </Field.Root>

            <Field.Root>
              <Controller
                name="state_registry_enabled"
                control={control}
                render={({ field }) => (
                  <Switch.Root
                    checked={field.value}
                    onCheckedChange={(d) => field.onChange(d.checked)}
                    disabled={!isEnabled}
                  >
                    <Switch.HiddenInput data-e2e="screening-state-registry-switch" />
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                    <Switch.Label>State Registry</Switch.Label>
                  </Switch.Root>
                )}
              />
            </Field.Root>

            {stateRegistryEnabled && (
              <Field.Root invalid={!!errors.state_code}>
                <Field.Label htmlFor="screening-state-code">
                  State Code
                </Field.Label>
                <InputField
                  id="screening-state-code"
                  {...register('state_code')}
                  maxLength={2}
                  placeholder="e.g. TX"
                  disabled={!isEnabled}
                  data-e2e="eg-tx-input"
                  aria-invalid={!!errors.state_code}
                  aria-describedby={
                    errors.state_code ? 'screening-state-code-error' : undefined
                  }
                />
                {errors.state_code && (
                  <Field.ErrorText
                    id="screening-state-code-error"
                    data-e2e="screening-state-code-error"
                  >
                    {errors.state_code.message
                      ? t(errors.state_code.message)
                      : null}
                  </Field.ErrorText>
                )}
              </Field.Root>
            )}

            <Box>
              <ButtonComponent
                visual="primary"
                type="submit"
                data-e2e="save-configuration-button"
              >
                Save Configuration
              </ButtonComponent>
            </Box>
          </VStack>
        </form>
      </FormProvider>
    </Box>
  );
}

// =============================================================================
// Employee Visit Reasons sub-tab
// =============================================================================

function EmployeeReasonsSection() {
  const { t } = useTranslation();
  const {
    isLoading,
    error,
    drawerOpen,
    setDrawerOpen,
    editItem,
    handleClose,
    handleSubmit,
    handleDelete,
    handleRowClick,
    initialValues,
    rows,
    columns,
  } = useEmployeeReasonTypeCrud();

  const methods = useForm<EmployeeReasonTypeWrite>({
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  const {
    register,
    control,
    formState: { errors },
  } = methods;

  useEffect(() => {
    methods.reset(initialValues);
  }, [initialValues]);

  const onSubmit = (values: EmployeeReasonTypeWrite) => {
    handleSubmit(values, methods.setError);
  };

  return (
    <Box>
      <Box px={5} pb={2} pt={4} display="flex" justifyContent="flex-end">
        <ButtonComponent
          visual="primary"
          onClick={() => setDrawerOpen(true)}
          data-e2e="add-employee-reason-button"
        >
          Add Employee Reason
        </ButtonComponent>
      </Box>
      <Box px={5} pb={5}>
        {isLoading ? (
          <LoadingPlaceholder />
        ) : error ? (
          <ErrorPlaceholder error={error} />
        ) : rows.length === 0 ? (
          <EmptyPlaceholder message="No employee visit reason types configured" />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            keyExtractor={(row) => row.id}
            onRowClick={handleRowClick}
          />
        )}
      </Box>

      <DrawerComponent
        isOpen={drawerOpen}
        onClose={handleClose}
        title={
          editItem ? 'Edit Employee Visit Reason' : 'Add Employee Visit Reason'
        }
        size="md"
        footer={
          <SimpleGrid columns={editItem ? 3 : 2} gap={4} width="100%">
            {editItem && (
              <ButtonComponent
                visual="alert"
                type="button"
                onClick={handleDelete}
                data-e2e="delete-button"
              >
                Delete
              </ButtonComponent>
            )}
            <ButtonComponent
              visual="secondary"
              type="button"
              onClick={handleClose}
              data-e2e="cancel-button"
            >
              Cancel
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              type="submit"
              form="employee-reason-form"
              data-e2e="save-button"
            >
              Save
            </ButtonComponent>
          </SimpleGrid>
        }
      >
        <FormProvider {...methods}>
          <form
            id="employee-reason-form"
            onSubmit={methods.handleSubmit(onSubmit)}
          >
            <VStack gap={4} align="stretch">
              <Field.Root invalid={!!errors.name}>
                <Field.Label htmlFor="employee-reason-name">Name</Field.Label>
                <InputField
                  id="employee-reason-name"
                  {...register('name')}
                  placeholder="e.g. Campus Patrol"
                  data-e2e="employee-reason-name-input"
                  aria-invalid={!!errors.name}
                  aria-describedby={
                    errors.name ? 'employee-reason-name-error' : undefined
                  }
                />
                {errors.name && (
                  <Field.ErrorText
                    id="employee-reason-name-error"
                    data-e2e="employee-reason-name-error"
                  >
                    {errors.name.message ? t(errors.name.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>
              <Field.Root invalid={!!errors.code}>
                <Field.Label htmlFor="employee-reason-code">Code</Field.Label>
                <InputField
                  id="employee-reason-code"
                  {...register('code')}
                  placeholder="e.g. campus_patrol"
                  data-e2e="employee-reason-code-input"
                  aria-invalid={!!errors.code}
                  aria-describedby={
                    errors.code ? 'employee-reason-code-error' : undefined
                  }
                />
                {errors.code && (
                  <Field.ErrorText
                    id="employee-reason-code-error"
                    data-e2e="employee-reason-code-error"
                  >
                    {errors.code.message ? t(errors.code.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>
              <Field.Root invalid={!!errors.description}>
                <Field.Label htmlFor="employee-reason-description">
                  Description
                </Field.Label>
                <TextareaField
                  id="employee-reason-description"
                  {...register('description')}
                  rows={2}
                  data-e2e="employee-reason-description-textarea"
                  aria-invalid={!!errors.description}
                  aria-describedby={
                    errors.description
                      ? 'employee-reason-description-error'
                      : undefined
                  }
                />
                {errors.description && (
                  <Field.ErrorText
                    id="employee-reason-description-error"
                    data-e2e="employee-reason-description-error"
                  >
                    {errors.description.message
                      ? t(errors.description.message)
                      : null}
                  </Field.ErrorText>
                )}
              </Field.Root>
              <Field.Root>
                <Controller
                  name="requires_supervisor_approval"
                  control={control}
                  render={({ field }) => (
                    <Switch.Root
                      checked={field.value}
                      onCheckedChange={(d) => field.onChange(d.checked)}
                    >
                      <Switch.HiddenInput data-e2e="employee-reason-requires-supervisor-approval-switch" />
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                      <Switch.Label>Requires Supervisor Approval</Switch.Label>
                    </Switch.Root>
                  )}
                />
              </Field.Root>
              <Field.Root>
                <Controller
                  name="is_active"
                  control={control}
                  render={({ field }) => (
                    <Switch.Root
                      checked={field.value}
                      onCheckedChange={(d) => field.onChange(d.checked)}
                    >
                      <Switch.HiddenInput data-e2e="employee-reason-is-active-switch" />
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                      <Switch.Label>Active</Switch.Label>
                    </Switch.Root>
                  )}
                />
              </Field.Root>
            </VStack>
          </form>
        </FormProvider>
      </DrawerComponent>
    </Box>
  );
}

// =============================================================================
// Car Rider Configuration Section
// =============================================================================

function CarRiderConfigSection() {
  const { t } = useTranslation();
  const {
    rows,
    columns,
    isLoading,
    error,
    drawerOpen,
    setDrawerOpen,
    editItem,
    handleClose,
    handleSubmit,
    handleDelete,
    handleRowClick,
    initialValues,
    sitesData,
  } = useCarRiderQueueCrud();
  const methods = useForm<z.infer<typeof carRiderFormSchema>>({
    defaultValues: initialValues,
    resolver: zodResolver(carRiderFormSchema),
    mode: 'onBlur',
  });

  const {
    register,
    control,
    setValue,
    watch,
    formState: { errors },
  } = methods;
  const resetForm = () => {
    methods.reset(initialValues);
    // handleClose()
  };
  useEffect(() => {
    resetForm();
  }, [initialValues, methods]);

  const onSubmit = (values: z.infer<typeof carRiderFormSchema>) => {
    handleSubmit(values, methods.setError);
  };

  const schoolOptions = useMemo(() => {
    return (sitesData?.results ?? []).map((site) => ({
      value: String(site.id),
      label: site.name,
    }));
  }, [sitesData]);

  const watchedSchoolId = watch('school');

  const [studentPage, setStudentPage] = useState(1);
  const [accumulatedStudents, setAccumulatedStudents] = useState<
    PlanningStudent[]
  >([]);
  const [lastStudentsData, setLastStudentsData] = useState<any>(null);

  useEffect(() => {
    setStudentPage(1);
    setAccumulatedStudents([]);
    setLastStudentsData(null);
  }, [watchedSchoolId]);

  const { data: studentsData, isFetching } = usePlanningStudents({
    page_size: 10,
    ordering: 'last_name',
    school: watchedSchoolId ? Number(watchedSchoolId) : undefined,
    page: studentPage,
  });

  useEffect(() => {
    if (studentsData?.results && studentsData !== lastStudentsData) {
      setLastStudentsData(studentsData);
      setAccumulatedStudents((prev) => {
        const existingIds = new Set(prev.map((s) => s.id));
        const newItems = studentsData.results.filter(
          (s) => !existingIds.has(s.id),
        );
        if (newItems.length === 0) return prev;
        return [...prev, ...newItems];
      });
    }
  }, [studentsData, lastStudentsData]);

  const handleScrollToEnd = () => {
    if (isFetching) return;
    const totalItems = studentsData?.count ?? 0;
    if (accumulatedStudents.length < totalItems) {
      setStudentPage((prev) => prev + 1);
    }
  };

  const studentOptions = useMemo(() => {
    if (!watchedSchoolId) return [];

    const selectedSchool = (sitesData?.results ?? []).find(
      (site) => Number(site.id) === Number(watchedSchoolId),
    );
    const schoolName = selectedSchool?.name;

    const options = accumulatedStudents
      .filter((s) => {
        if (s.school_name) {
          return s.school_name === schoolName;
        }
        return Number(s.school) === Number(watchedSchoolId);
      })
      .map((s) => {
        const fullName =
          `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim() ||
          s.external_reference_id;
        return {
          value: String(s.id),
          label: s.grade ? `${fullName} (Grade ${s.grade})` : fullName,
        };
      });

    if (
      editItem &&
      editItem.student &&
      !options.some((o) => o.value === String(editItem.student))
    ) {
      options.push({
        value: String(editItem.student),
        label: editItem.student_name || `Student #${editItem.student}`,
      });
    }

    return options;
  }, [accumulatedStudents, editItem, watchedSchoolId]);

  const statusOptions = [
    { value: 'waiting', label: 'Waiting' },
    { value: 'called', label: 'Called' },
    { value: 'picked_up', label: 'Picked Up' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <Box>
      <Box px={5} pb={2} pt={4} display="flex" justifyContent="flex-end">
        <ButtonComponent
          visual="primary"
          onClick={() => setDrawerOpen(true)}
          data-e2e="add-car-rider-button"
        >
          Add Entry
        </ButtonComponent>
      </Box>
      <Box px={5} pb={5}>
        {isLoading ? (
          <LoadingPlaceholder />
        ) : error ? (
          <ErrorPlaceholder error={error} />
        ) : rows.length === 0 ? (
          <EmptyPlaceholder message="No car rider queue entries" />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            keyExtractor={(row) => row.id}
            onRowClick={handleRowClick}
          />
        )}
      </Box>

      <DrawerComponent
        isOpen={drawerOpen}
        onClose={() => {
          resetForm();
          handleClose();
        }}
        title={editItem ? 'Edit Car Rider Entry' : 'Add Car Rider Entry'}
        size="md"
        footer={
          <SimpleGrid columns={editItem ? 3 : 2} gap={4} width="100%">
            {editItem && (
              <ButtonComponent
                visual="alert"
                type="button"
                onClick={handleDelete}
                data-e2e="delete-button"
              >
                Delete
              </ButtonComponent>
            )}
            <ButtonComponent
              visual="secondary"
              type="button"
              onClick={() => {
                resetForm();
                handleClose();
              }}
              data-e2e="cancel-button"
            >
              Cancel
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              type="submit"
              form="car-rider-form"
              data-e2e="save-button"
            >
              Save
            </ButtonComponent>
          </SimpleGrid>
        }
      >
        <FormProvider {...methods}>
          <form id="car-rider-form" onSubmit={methods.handleSubmit(onSubmit)}>
            <VStack gap={4} align="stretch">
              <Field.Root invalid={!!errors.school}>
                <Field.Label htmlFor="car-rider-school">School</Field.Label>
                <Controller
                  name="school"
                  control={control}
                  render={({ field }) => (
                    <SelectInput
                      id="car-rider-school"
                      value={field.value ? String(field.value) : ''}
                      option={schoolOptions}
                      onChange={(val: string) => {
                        field.onChange(Number(val));
                        setValue('student', 0);
                      }}
                      searchable={true}
                      placeholder="Select School"
                      e2eTitle="car-rider-school-select"
                    />
                  )}
                />
                {errors.school && (
                  <Field.ErrorText
                    id="school-error"
                    data-e2e="car-rider-school-error"
                  >
                    {errors.school.message ? t(errors.school.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={!!errors.student}>
                <Field.Label htmlFor="car-rider-student">Student</Field.Label>
                <Controller
                  name="student"
                  control={control}
                  render={({ field }) => (
                    <SelectInput
                      id="car-rider-student"
                      value={field.value ? String(field.value) : ''}
                      option={studentOptions}
                      onChange={(val: string) => field.onChange(Number(val))}
                      searchable={true}
                      placeholder={
                        watchedSchoolId
                          ? 'Select Student'
                          : 'Select a school first'
                      }
                      e2eTitle="car-rider-student-select"
                      onScrollToEnd={handleScrollToEnd}
                    />
                  )}
                />
                {errors.student && (
                  <Field.ErrorText
                    id="student-error"
                    data-e2e="car-rider-student-error"
                  >
                    {errors.student.message ? t(errors.student.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={!!errors.status}>
                <Field.Label htmlFor="car-rider-status">Status</Field.Label>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <SelectInput
                      id="car-rider-status"
                      value={field.value}
                      option={statusOptions}
                      onChange={(val: string) => field.onChange(val)}
                      searchable={false}
                      e2eTitle="car-rider-status-select"
                    />
                  )}
                />
                {errors.status && (
                  <Field.ErrorText
                    id="status-error"
                    data-e2e="car-rider-status-error"
                  >
                    {errors.status.message ? t(errors.status.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={!!errors.queued_at}>
                <Field.Label htmlFor="car-rider-queued-at">
                  Queued At
                </Field.Label>
                <InputField
                  id="car-rider-queued-at"
                  type="datetime-local"
                  {...register('queued_at')}
                  data-e2e="car-rider-queued-at-input"
                />
                {errors.queued_at && (
                  <Field.ErrorText
                    id="queued-at-error"
                    data-e2e="car-rider-queued-at-error"
                  >
                    {errors.queued_at.message
                      ? t(errors.queued_at.message)
                      : null}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={!!errors.notes}>
                <Field.Label htmlFor="car-rider-notes">Notes</Field.Label>
                <TextareaField
                  id="car-rider-notes"
                  {...register('notes')}
                  rows={3}
                  data-e2e="car-rider-notes-textarea"
                />
                {errors.notes && (
                  <Field.ErrorText
                    id="notes-error"
                    data-e2e="car-rider-notes-error"
                  >
                    {errors.notes.message ? t(errors.notes.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>
            </VStack>
          </form>
        </FormProvider>
      </DrawerComponent>
    </Box>
  );
}

// =============================================================================
// Pickup Delegation Configuration Section
// =============================================================================

function PickupDelegationConfigSection() {
  const { t } = useTranslation();
  const methods = useForm<z.infer<typeof pickupDelegationFormSchema>>({
    resolver: zodResolver(pickupDelegationFormSchema),
    mode: 'onBlur',
  });

  const {
    register,
    control,
    setValue,
    watch,
    formState: { errors },
  } = methods;

  const watchedSchoolId = watch('school');
  const [studentPage, setStudentPage] = useState(1);
  const [accumulatedStudents, setAccumulatedStudents] = useState<
    PlanningStudent[]
  >([]);
  const [lastStudentsData, setLastStudentsData] = useState<any>(null);

  useEffect(() => {
    setStudentPage(1);
    setAccumulatedStudents([]);
    setLastStudentsData(null);
  }, [watchedSchoolId]);

  const { data: studentsData, isFetching } = usePlanningStudents({
    page_size: 10,
    ordering: 'last_name',
    school: watchedSchoolId ? Number(watchedSchoolId) : undefined,
    page: studentPage,
  });

  useEffect(() => {
    if (studentsData?.results && studentsData !== lastStudentsData) {
      setLastStudentsData(studentsData);
      setAccumulatedStudents((prev) => {
        const existingIds = new Set(prev.map((s) => s.id));
        const newItems = studentsData.results.filter(
          (s) => !existingIds.has(s.id),
        );
        if (newItems.length === 0) return prev;
        return [...prev, ...newItems];
      });
    }
  }, [studentsData, lastStudentsData]);

  const handleScrollToEnd = () => {
    if (isFetching) return;
    const totalItems = studentsData?.count ?? 0;
    if (accumulatedStudents.length < totalItems) {
      setStudentPage((prev) => prev + 1);
    }
  };

  const {
    rows,
    columns,
    isLoading,
    error,
    drawerOpen,
    setDrawerOpen,
    editItem,
    handleClose,
    handleSubmit,
    handleDelete,
    handleRowClick,
    initialValues,
    sitesData,
  } = usePickupDelegationCrud({ students: accumulatedStudents });
  const resetForm = () => {
    methods.reset(initialValues);
  };
  useEffect(() => {
    resetForm();
  }, [methods]);
  const onSubmit = (values: z.infer<typeof pickupDelegationFormSchema>) => {
    handleSubmit(values, methods.setError);
  };

  const schoolOptions = useMemo(() => {
    return (sitesData?.results ?? []).map((site) => ({
      value: String(site.id),
      label: site.name,
    }));
  }, [sitesData]);
  const studentOptions = useMemo(() => {
    if (!watchedSchoolId) return [];

    const selectedSchool = (sitesData?.results ?? []).find(
      (site) => Number(site.id) === Number(watchedSchoolId),
    );
    const schoolName = selectedSchool?.name;

    const options = accumulatedStudents
      .filter((s) => {
        if (s.school_name) {
          return s.school_name === schoolName;
        }
        return Number(s.school) === Number(watchedSchoolId);
      })
      .map((s) => {
        const fullName =
          `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim() ||
          s.external_reference_id;
        return {
          value: String(s.id),
          label: s.grade ? `${fullName} (Grade ${s.grade})` : fullName,
        };
      });

    if (
      editItem &&
      editItem.student &&
      !options.some((o) => o.value === String(editItem.student))
    ) {
      options.push({
        value: String(editItem.student),
        label: editItem.student_name || `Student #${editItem.student}`,
      });
    }

    return options;
  }, [accumulatedStudents, editItem, watchedSchoolId]);

  return (
    <Box>
      <Box px={5} pb={2} pt={4} display="flex" justifyContent="flex-end">
        <ButtonComponent
          visual="primary"
          onClick={() => setDrawerOpen(true)}
          data-e2e="add-pickup-delegation-button"
        >
          Add Delegation
        </ButtonComponent>
      </Box>
      <Box px={5} pb={5}>
        {isLoading ? (
          <LoadingPlaceholder />
        ) : error ? (
          <ErrorPlaceholder error={error} />
        ) : rows.length === 0 ? (
          <EmptyPlaceholder message="No pickup delegations configured" />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            keyExtractor={(row) => row.id}
            onRowClick={handleRowClick}
          />
        )}
      </Box>

      <DrawerComponent
        isOpen={drawerOpen}
        onClose={() => {
          resetForm();
          handleClose();
        }}
        title={editItem ? 'Edit Pickup Delegation' : 'Add Pickup Delegation'}
        size="md"
        footer={
          <SimpleGrid columns={editItem ? 3 : 2} gap={4} width="100%">
            {editItem && (
              <ButtonComponent
                visual="alert"
                type="button"
                onClick={handleDelete}
                data-e2e="delete-button"
              >
                Delete
              </ButtonComponent>
            )}
            <ButtonComponent
              visual="secondary"
              type="button"
              onClick={() => {
                resetForm();
                handleClose();
              }}
              data-e2e="cancel-button"
            >
              Cancel
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              type="submit"
              form="pickup-delegation-form"
              data-e2e="save-button"
            >
              Save
            </ButtonComponent>
          </SimpleGrid>
        }
      >
        <FormProvider {...methods}>
          <form
            id="pickup-delegation-form"
            onSubmit={methods.handleSubmit(onSubmit)}
          >
            <VStack gap={4} align="stretch">
              <Field.Root invalid={!!errors.school}>
                <Field.Label htmlFor="pickup-delegation-school">
                  School
                </Field.Label>
                <Controller
                  name="school"
                  control={control}
                  render={({ field }) => (
                    <SelectInput
                      id="pickup-delegation-school"
                      value={field.value ? String(field.value) : ''}
                      option={schoolOptions}
                      onChange={(val: string) => {
                        field.onChange(Number(val));
                        setValue('student', 0);
                      }}
                      searchable={true}
                      placeholder="Select School"
                      e2eTitle="pickup-delegation-school-select"
                    />
                  )}
                />
                {errors.school && (
                  <Field.ErrorText
                    id="school-error"
                    data-e2e="pickup-delegation-school-error"
                  >
                    {errors.school.message ? t(errors.school.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={!!errors.student}>
                <Field.Label htmlFor="pickup-delegation-student">
                  Student
                </Field.Label>
                <Controller
                  name="student"
                  control={control}
                  render={({ field }) => (
                    <SelectInput
                      id="pickup-delegation-student"
                      value={field.value ? String(field.value) : ''}
                      option={studentOptions}
                      onChange={(val: string) => field.onChange(Number(val))}
                      searchable={true}
                      placeholder={
                        watchedSchoolId
                          ? 'Select Student'
                          : 'Select a school first'
                      }
                      e2eTitle="pickup-delegation-student-select"
                      onScrollToEnd={handleScrollToEnd}
                    />
                  )}
                />
                {errors.student && (
                  <Field.ErrorText
                    id="student-error"
                    data-e2e="pickup-delegation-student-error"
                  >
                    {errors.student.message ? t(errors.student.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={!!errors.delegate_name}>
                <Field.Label htmlFor="delegate-name">Delegate Name</Field.Label>
                <InputField
                  id="delegate-name"
                  {...register('delegate_name')}
                  placeholder="e.g. John Doe"
                  data-e2e="delegate-name-input"
                />
                {errors.delegate_name && (
                  <Field.ErrorText
                    id="delegate-name-error"
                    data-e2e="pickup-delegation-delegate-name-error"
                  >
                    {errors.delegate_name.message
                      ? t(errors.delegate_name.message)
                      : null}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={!!errors.delegate_phone}>
                <Field.Label htmlFor="delegate-phone">
                  Delegate Phone
                </Field.Label>
                <InputField
                  id="delegate-phone"
                  {...register('delegate_phone')}
                  placeholder="e.g. 555-0199"
                  data-e2e="delegate-phone-input"
                />
                {errors.delegate_phone && (
                  <Field.ErrorText
                    id="delegate-phone-error"
                    data-e2e="pickup-delegation-delegate-phone-error"
                  >
                    {errors.delegate_phone.message
                      ? t(errors.delegate_phone.message)
                      : null}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={!!errors.delegate_email}>
                <Field.Label htmlFor="delegate-email">
                  Delegate Email
                </Field.Label>
                <InputField
                  id="delegate-email"
                  type="email"
                  {...register('delegate_email')}
                  placeholder="e.g. john@example.com"
                  data-e2e="delegate-email-input"
                />
                {errors.delegate_email && (
                  <Field.ErrorText
                    id="delegate-email-error"
                    data-e2e="pickup-delegation-delegate-email-error"
                  >
                    {errors.delegate_email.message
                      ? t(errors.delegate_email.message)
                      : null}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={!!errors.relationship}>
                <Field.Label htmlFor="delegate-relationship">
                  Relationship
                </Field.Label>
                <InputField
                  id="delegate-relationship"
                  {...register('relationship')}
                  placeholder="e.g. Uncle"
                  data-e2e="delegate-relationship-input"
                />
                {errors.relationship && (
                  <Field.ErrorText
                    id="relationship-error"
                    data-e2e="pickup-delegation-relationship-error"
                  >
                    {errors.relationship.message
                      ? t(errors.relationship.message)
                      : null}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={!!errors.valid_from}>
                <Field.Label htmlFor="valid-from">Valid From</Field.Label>
                <InputField
                  id="valid-from"
                  type="datetime-local"
                  {...register('valid_from')}
                  data-e2e="valid-from-input"
                />
                {errors.valid_from && (
                  <Field.ErrorText
                    id="valid-from-error"
                    data-e2e="pickup-delegation-valid-from-error"
                  >
                    {errors.valid_from.message
                      ? t(errors.valid_from.message)
                      : null}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={!!errors.valid_until}>
                <Field.Label htmlFor="valid-until">
                  Valid Until (Optional)
                </Field.Label>
                <InputField
                  id="valid-until"
                  type="datetime-local"
                  {...register('valid_until')}
                  data-e2e="valid-until-input"
                />
                {errors.valid_until && (
                  <Field.ErrorText
                    id="valid-until-error"
                    data-e2e="pickup-delegation-valid-until-error"
                  >
                    {errors.valid_until.message
                      ? t(errors.valid_until.message)
                      : null}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root>
                <Controller
                  name="is_active"
                  control={control}
                  render={({ field }) => (
                    <Switch.Root
                      checked={field.value}
                      onCheckedChange={(d) => field.onChange(d.checked)}
                    >
                      <Switch.HiddenInput data-e2e="pickup-delegation-active-switch" />
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                      <Switch.Label>Active</Switch.Label>
                    </Switch.Root>
                  )}
                />
              </Field.Root>

              <Field.Root invalid={!!errors.notes}>
                <Field.Label htmlFor="pickup-delegation-notes">
                  Notes
                </Field.Label>
                <TextareaField
                  id="pickup-delegation-notes"
                  {...register('notes')}
                  rows={3}
                  data-e2e="pickup-delegation-notes-textarea"
                />
                {errors.notes && (
                  <Field.ErrorText
                    id="notes-error"
                    data-e2e="pickup-delegation-notes-error"
                  >
                    {errors.notes.message ? t(errors.notes.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>
            </VStack>
          </form>
        </FormProvider>
      </DrawerComponent>
    </Box>
  );
}
// =============================================================================
// Main Component
// =============================================================================

export function VisitorManagementContent({
  initialSubTab,
}: {
  initialSubTab?: string;
}) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canManage, isLoading: permissionsLoading } = useVisitorPermissions();

  const localizedVmTabs: TabItem[] = useMemo(
    () =>
      vmTabs.map((tab) => ({
        ...tab,
        label: t(`visitorMgmt.subtabs.${tab.value}`, tab.label),
      })),
    [t],
  );

  // Redirect legacy subtab values
  useEffect(() => {
    const subTabRedirectMap: Record<string, string> = {
      reasons: 'visit-reasons',
      badges: 'badge-templates',
    };
    const subtab = searchParams.get('subtab');
    if (subtab && subTabRedirectMap[subtab]) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('subtab', subTabRedirectMap[subtab]);
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  const activeTab =
    searchParams.get('subtab')?.toLowerCase().replace(/\s+/g, '-') ||
    initialSubTab?.toLowerCase().replace(/\s+/g, '-') ||
    'visit-reasons';

  const handleTabChange = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('subtab', tab);
      next.delete('id');
      next.delete('public_id');
      return next;
    });
  };

  if (permissionsLoading) {
    return <LoadingPlaceholder />;
  }

  if (!canManage) {
    return (
      <Center py={12}>
        <Text color="text.secondary" data-e2e="vm-config-access-denied">
          {t(
            'visitorMgmt.accessDenied',
            'You do not have permission to configure Visitor Management.',
          )}
        </Text>
      </Center>
    );
  }

  return (
    <Box>
      <TabMenu
        tabs={localizedVmTabs}
        activeTab={activeTab}
        onChange={handleTabChange}
      />
      {activeTab === 'visit-reasons' && <ReasonTypesSection />}
      {activeTab === 'employee-reasons' && <EmployeeReasonsSection />}
      {activeTab === 'categories' && <CategoryTypesSection />}
      {activeTab === 'badge-templates' && <BadgeTemplatesSection />}
      {activeTab === 'screening' && <ScreeningConfigSection />}
      {activeTab === 'roster' && <RosterContent />}
      {activeTab === 'car-rider' && <CarRiderConfigSection />}
      {activeTab === 'pickup-delegation' && <PickupDelegationConfigSection />}
    </Box>
  );
}
